import { getPublicEnv } from './public-env';

type PublicEnv = ReturnType<typeof getPublicEnv>;
type PublicEnvKey = keyof PublicEnv;

function isPublicEnvPlaceholder<TKey extends PublicEnvKey>({
  key,
  value,
}: {
  key: TKey;
  value: PublicEnv[TKey];
}): boolean {
  return value === key || value === `_${String(key)}`;
}

function getServerConfigValue<TKey extends PublicEnvKey>(
  key: TKey,
): PublicEnv[TKey] | undefined {
  if (typeof window !== 'undefined') {
    return undefined;
  }

  const value = process.env[`_${String(key)}`] ?? process.env[String(key)];

  return isPublicEnvPlaceholder({ key, value: value as PublicEnv[TKey] })
    ? undefined
    : (value as PublicEnv[TKey] | undefined);
}

export function getConfigValue<TKey extends PublicEnvKey>(
  key: TKey,
): PublicEnv[TKey] {
  try {
    const serverValue = getServerConfigValue(key);

    if (serverValue !== undefined) {
      return serverValue;
    }

    const value = getPublicEnv()[key];
    const normalizedValue = isPublicEnvPlaceholder({ key, value })
      ? undefined
      : value;

    return normalizedValue as PublicEnv[TKey];
  } catch {
    return undefined as PublicEnv[TKey];
  }
}
